export const MOCK_MARKET_STORAGE_KEY = "replyn:mock-market";

export const MOCK_MARKETS = ["nepal", "uae", "usa"] as const;

export type MockMarket = (typeof MOCK_MARKETS)[number];
export type MockMarketSetting = "auto" | MockMarket;

interface MockMarketProfile {
  label: string;
  currency: "NPR" | "AED" | "USD";
  locale: "ne" | "en";
  timezone: string;
  region: "NP" | "AE" | "US";
  city: string;
  location: string;
  organizationName: string;
  organizationSlug: string;
  organizationPhone: string;
  organizationWebsite: string;
  workspaceNames: string[];
  cities: string[];
  /** Adapts the NPR-sized storefront fixtures without pretending to be a live exchange rate. */
  storefrontAmountScale: number;
}

const MARKET_PROFILES: Record<MockMarket, MockMarketProfile> = {
  nepal: {
    label: "Nepal",
    currency: "NPR",
    locale: "ne",
    timezone: "Asia/Kathmandu",
    region: "NP",
    city: "Kathmandu",
    location: "Kathmandu, Nepal",
    organizationName: "Himalaya Aesthetics",
    organizationSlug: "himalaya-aesthetics",
    organizationPhone: "+977 1 555 0198",
    organizationWebsite: "https://himalayaaesthetics.com.np",
    workspaceNames: ["Kathmandu Clinic", "Lalitpur Branch", "Test Workspace"],
    cities: ["Kathmandu", "Lalitpur", "Bhaktapur", "Pokhara", "Chitwan"],
    storefrontAmountScale: 1,
  },
  uae: {
    label: "UAE",
    currency: "AED",
    locale: "en",
    timezone: "Asia/Dubai",
    region: "AE",
    city: "Dubai",
    location: "Dubai, UAE",
    organizationName: "Noor Aesthetics",
    organizationSlug: "noor-aesthetics",
    organizationPhone: "+971 4 555 0198",
    organizationWebsite: "https://nooraesthetics.ae",
    workspaceNames: ["Dubai Clinic", "Jumeirah Branch", "Test Workspace"],
    cities: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Al Ain"],
    storefrontAmountScale: 0.14,
  },
  usa: {
    label: "USA",
    currency: "USD",
    locale: "en",
    timezone: "America/New_York",
    region: "US",
    city: "New York",
    location: "New York, USA",
    organizationName: "Meridian Aesthetics",
    organizationSlug: "meridian-aesthetics",
    organizationPhone: "+1 (212) 555-0198",
    organizationWebsite: "https://meridianaesthetics.com",
    workspaceNames: ["Manhattan Clinic", "Brooklyn Branch", "Test Workspace"],
    cities: ["New York", "Los Angeles", "Chicago", "Miami", "Boston"],
    storefrontAmountScale: 0.05,
  },
};

export function detectMockMarket(timezone: string | null): MockMarket {
  if (timezone === "Asia/Kathmandu" || timezone === "Asia/Katmandu") {
    return "nepal";
  }

  return timezone === "Asia/Dubai" ? "uae" : "usa";
}

export function getBrowserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

export function resolveMockMarket(
  setting: string | null,
  timezone: string | null,
): MockMarket {
  return MOCK_MARKETS.includes(setting as MockMarket)
    ? (setting as MockMarket)
    : detectMockMarket(timezone);
}

export function getMockMarketSetting(): MockMarketSetting {
  try {
    const setting = localStorage.getItem(MOCK_MARKET_STORAGE_KEY);

    return setting === "auto" || MOCK_MARKETS.includes(setting as MockMarket)
      ? (setting as MockMarketSetting)
      : "auto";
  } catch {
    return "auto";
  }
}

export function setMockMarketSetting(setting: MockMarketSetting): void {
  localStorage.setItem(MOCK_MARKET_STORAGE_KEY, setting);
}

export function getMockMarket(): MockMarket {
  // The brand run scripts (.env.replyn / .env.clinicpilot) pin the market, so a
  // stale localStorage pick can't drag the demo back to another region.
  return resolveMockMarket(
    import.meta.env.VITE_MOCK_MARKET ?? getMockMarketSetting(),
    getBrowserTimezone(),
  );
}

export function getMockMarketProfile(
  market: MockMarket = getMockMarket(),
): MockMarketProfile {
  return MARKET_PROFILES[market];
}

export function localizeMockStorefrontAmount(
  value: number,
  market: MockMarket = getMockMarket(),
): number {
  return Number(
    (value * MARKET_PROFILES[market].storefrontAmountScale).toFixed(2),
  );
}
